import MainLayout from '@layouts/MainLayout';

import QuantityField from '@components/Forms/QuanityField';
import Breadcrumb from '@components/Common/Breadcrumb';

import styles from './style.module.css';
import {useRouter} from 'next/router';
import Button from '@components/Forms/Button';

import {sumPrice, toThousand, toThousandLocaleCurrency} from '@lib/utils';
import ProductDisplayAccessories from './ProductDisplayAccessories';
import ProductAccessoryLoaderList from '../../content-loader/ProductAccessoryLoader';
import ProductDisplayImage from './ProductDisplayImage';

import {useCart, useProductDisplay} from '@contexts/index';
import ProductDisplayTab from './ProductDisplayTab';
import {useTranslation} from 'next-i18next';
import {ProductDisplaysEnum} from '@enums/product-display';
import ImageCarousel from '@components/ImageCarousel';
import HeadMeta from '@components/Header/HeadMeta';
import FormGroup from '@components/Forms/FormGroup';
import TextField from '@components/Forms/TextField';
import {ReactNode} from 'react';
import {ProductGroupDisplayResponseData} from '@interfaces/product-group-display';
import {LocaleEnum} from '@enums/locale';

export default function ProductGroupDisplayComponent({
  data,
  ProductVarianceComponent,
  hasAccessories = true,
}: {
  data: ProductGroupDisplayResponseData;
  hasAccessories?: boolean;
  ProductVarianceComponent: ReactNode;
}) {
  const {t} = useTranslation(['product-display', 'common']);
  const router = useRouter();
  const locale = router.locale as unknown as LocaleEnum;
  const {breadcrumb, images, tabs, content_container, type} = data;
  const product_group_name = data['product_group_name'];
  const product_group_description = data['product_group_description'];
  const product_group_information = data['product_group_information'];

  const {state, setQuantity, setKeyValuePairs} = useProductDisplay();

  const {
    product,
    quantity,
    total_discount_price_vat_included,
    key_value_pairs,
    load_product_prices,
    linked_products,
  } = state;

  const {
    addItemToCart,
    state: {addItemLoading},
  } = useCart();

  const handleAddToCart = () => {
    const linked_products: {
      product_id: number;
      quantity: number;
      type: 'product_accessories';
    }[] = Object.keys(state.linked_products).map(product_group_id => {
      const accessories = state.linked_products[product_group_id];
      return {
        product_id: accessories.id,
        quantity,
        type: 'product_accessories',
      };
    });
    const items = {
      product_id: product!.id,
      quantity,
      key_value_pairs,
      linked_products,
    };
    addItemToCart({items});
  };

  return (
    <MainLayout>
      <HeadMeta
        title={
          data.content_container['seo_page_title'] || data['product_group_name']
        }
        description={data.content_container['seo_description']}
        keywords={data.content_container['seo_keywords']}
        canonical_url={data.content_container['canonical_url']}
      />
      <Breadcrumb
        baseUrl={t('product_base_url')}
        classNames={styles.productDisplayBreadcrumb}
        justifyStart={true}
        list={breadcrumb}
      />
      <div className="container">
        <div className={`${styles.productFamilyContainer} mb-5 `}>
          <div className="row">
            <div
              className="col-md-6 col-lg-7 sticky-md-top align-self-start"
              style={{display: 'relative'}}
            >
              {type?.toLowerCase() ===
              ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_ART_DOOR.toLowerCase() ? (
                <ImageCarousel images={images} />
              ) : (
                <ProductDisplayImage src={images[0]?.image} />
              )}
            </div>
            <div className="col-md-6 col-lg-5">
              <div className={`${styles.productOverview}`}>
                <h1 title={`${product?.uid || 'Loading...'}`}>
                  {product_group_name}
                </h1>
                <div
                  className={`${styles.productInformation}`}
                  dangerouslySetInnerHTML={{
                    __html: product_group_description,
                  }}
                ></div>
                <div className={`${styles.priceInformation}`}>
                  {product?.unit_price_vat_included !==
                    product?.discount_unit_price_vat_included && (
                    <h5
                      className={`${styles.priceSlash} ${
                        load_product_prices && styles.blurred
                      } `}
                    >
                      {toThousandLocaleCurrency(
                        product?.unit_price_vat_included ?? 0,
                        locale
                      )}
                    </h5>
                  )}
                  <h4
                    className={`${
                      (load_product_prices || !product?.id) && styles.blurred
                    }`}
                  >
                    {toThousandLocaleCurrency(
                      product?.discount_unit_price_vat_included ?? 0,
                      locale
                    )}
                    {(product?.vat_percentage || product?.vat_text) && (
                      <span className={styles.productVat}>
                        /
                        {product.vat_percentage > 0 && (
                          <>
                            {toThousandLocaleCurrency(
                              product?.discount_unit_price_vat_excluded ?? 0,
                              locale
                            )}{' '}
                            {t('vat_exclusive')}
                          </>
                        )}
                      </span>
                    )}
                  </h4>
                </div>
                {ProductVarianceComponent}
                {!!product?.id && (
                  <p className={styles.itemCode}>UID: {product!.uid}</p>
                )}
                {hasAccessories &&
                  (!product?.id ? (
                    <ProductAccessoryLoaderList count={2} />
                  ) : (
                    <ProductDisplayAccessories />
                  ))}
                <div className={`row ${styles.productTotals}`}>
                  <FormGroup>
                    <label htmlFor="">
                      {t<string>('additional_information')}
                    </label>
                    <TextField
                      placeholder={t('additional_information')}
                      onChange={e => {
                        setKeyValuePairs({
                          [t('additional_information')]: e.target.value,
                        });
                      }}
                    />
                  </FormGroup>
                </div>
                <div className={`row ${styles.productTotals}`}>
                  <div className={`${styles.priceInformation}`}>
                    <p> {t<string>('total_price')}:</p>
                    <h4
                      className={` ${
                        (load_product_prices || !product?.id) && styles.blurred
                      }`}
                    >
                      {toThousandLocaleCurrency(
                        Number(total_discount_price_vat_included ?? 0) +
                          sumPrice(
                            Object.values(linked_products || []),
                            'total_discount_price_vat_included'
                          ),
                        locale
                      )}
                    </h4>
                  </div>
                  <div className={`col-md-12 ${styles.addToCart}`}>
                    <div className={`${styles.productQuantity}`}>
                      <label htmlFor="">{t<string>('quantity')}:</label>
                      <QuantityField
                        onValueChanged={value => {
                          setQuantity(value);
                        }}
                        initialValue={1}
                      />
                    </div>
                    <Button
                      disabled={
                        addItemLoading || !product?.id || load_product_prices
                      }
                      onClick={handleAddToCart}
                    >
                      {t<string>('add_to_cart')}{' '}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`${styles.productDetail} row`}
            style={{backgroundColor: '#fff'}}
          >
            <div className="col-md-7">
              <ProductDisplayTab
                productName={product_group_name}
                images={images.slice(1, 5)}
                content_container={content_container}
                tabs={tabs}
                productGroupUID={data.uid}
              />
            </div>
            <div className="col-md-5">
              <div className={`${styles.productDescription}`}>
                <h3>{t<string>('warranty')}</h3>
                <div
                  className={`${styles.productDescriptionContent}`}
                  dangerouslySetInnerHTML={{
                    __html: product_group_information,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
